import React from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import { TOKENS, pointTier } from '../theme';
import ZoomablePhoto from '../shared/ZoomablePhoto';
import WriteGuard from '../../global/ReadOnly/WriteGuard';
import { formatPointTime } from '../shared/auditFormat';

// Points Report Audit Mode — mirrors BonusAuditMode. One photo-bearing point
// entry at a time. Approve/Flag, auto-advance, photo zoom, duplicate warning.
// `points` here is the audit subset (entries that carry a photo); decisions
// are keyed POINT:pendingId.
export default function PointsAuditMode({
  open,
  onClose,
  onComplete,
  points,
  duplicateFlags,
  decisions,
  auditIndex,
  onIndexChange,
  onDecision,
}) {
  const [zoomOpen, setZoomOpen] = React.useState(false);

  if (!open || !points?.length) return null;
  const point = points[auditIndex];
  if (!point) return null;
  const flag = duplicateFlags.get(point.pendingId);
  const myDecision = decisions[`POINT:${point.pendingId}`];
  // Only count decisions whose pendingId is still present in the current list.
  const idSet = new Set(points.map((p) => p.pendingId));
  const reviewedCount = Object.keys(decisions).filter(
    (k) => k.startsWith('POINT:') && idSet.has(k.slice('POINT:'.length))
  ).length;
  const customerName = point.customer;
  const tier = pointTier(point.points);
  const photoUrl = point.photoUrl || point.vipPhotoUrl;

  const isLast = auditIndex === points.length - 1;
  const goPrev = () => onIndexChange(Math.max(0, auditIndex - 1));
  const goNext = () =>
    onIndexChange(Math.min(points.length - 1, auditIndex + 1));
  const handleComplete = () => (onComplete ? onComplete() : onClose());
  const handleApprove = () => {
    onDecision(point, 'APPROVED');
    setTimeout(goNext, 0);
  };
  const handleFlag = () => {
    onDecision(point, 'FLAGGED');
    setTimeout(goNext, 0);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar
        position="relative"
        sx={{
          background: '#fff',
          color: TOKENS.slate900,
          boxShadow: 0,
          borderBottom: `1px solid ${TOKENS.slate200}`,
        }}
      >
        <Toolbar sx={{ minHeight: 48 }}>
          <IconButton edge="start" onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: TOKENS.slate500,
              }}
            >
              Points Audit
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {auditIndex + 1} of {points.length}
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: 10, fontWeight: 700, color: TOKENS.slate500 }}
          >
            {reviewedCount}/{points.length}
          </Typography>
        </Toolbar>
        <Box
          sx={{ height: 4, background: TOKENS.slate200, position: 'relative' }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: `${(reviewedCount / points.length) * 100}%`,
              background: TOKENS.emerald600,
              transition: 'width 0.2s',
            }}
          />
        </Box>
      </AppBar>
      <Box sx={{ flex: 1, overflowY: 'auto', background: TOKENS.slate50 }}>
        {flag && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              background:
                flag.severity === 'high' ? TOKENS.red50 : TOKENS.amber50,
              borderBottom: `1px solid ${
                flag.severity === 'high' ? TOKENS.red200 : TOKENS.amber200
              }`,
            }}
          >
            <WarningAmberIcon
              sx={{
                color:
                  flag.severity === 'high' ? TOKENS.red600 : TOKENS.amber600,
                fontSize: 20,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color:
                    flag.severity === 'high' ? TOKENS.red900 : TOKENS.amber900,
                }}
              >
                {flag.severity === 'high'
                  ? 'Possible duplicate photo detected'
                  : 'Suspicious pattern detected'}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  mt: 0.25,
                  color:
                    flag.severity === 'high' ? TOKENS.red700 : TOKENS.amber700,
                }}
              >
                {flag.reason}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={`${flag.confidence}%`}
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                background:
                  flag.severity === 'high' ? TOKENS.red200 : TOKENS.amber200,
                color:
                  flag.severity === 'high' ? TOKENS.red900 : TOKENS.amber900,
              }}
            />
          </Box>
        )}
        {myDecision && (
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background:
                myDecision === 'APPROVED' ? TOKENS.emerald50 : TOKENS.red50,
              borderBottom: `1px solid ${
                myDecision === 'APPROVED' ? TOKENS.emerald200 : TOKENS.red200
              }`,
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background:
                  myDecision === 'APPROVED' ? TOKENS.emerald600 : TOKENS.red600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {myDecision === 'APPROVED' ? (
                <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
              ) : (
                <CloseIcon sx={{ color: '#fff', fontSize: 14 }} />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color:
                  myDecision === 'APPROVED' ? TOKENS.emerald700 : TOKENS.red700,
              }}
            >
              {myDecision === 'APPROVED' ? 'Approved' : 'Flagged for review'}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { md: 2 },
            maxWidth: 1400,
            mx: 'auto',
            width: '100%',
            px: { md: 2 },
            py: { md: 2 },
          }}
        >
          <Box
            component={photoUrl ? 'button' : 'div'}
            onClick={() => photoUrl && setZoomOpen(true)}
            sx={{
              width: { xs: '100%', md: 'auto' },
              flex: { md: '0 0 40%' },
              height: { xs: '40vh', md: 'auto' },
              aspectRatio: { md: '3/4' },
              maxHeight: { md: 'calc(100vh - 160px)' },
              background: TOKENS.slate900,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              borderRadius: { md: 1 },
              p: 0,
              cursor: photoUrl ? 'zoom-in' : 'default',
            }}
          >
            {photoUrl ? (
              <Box
                component="img"
                src={photoUrl}
                alt=""
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                No photo
              </Typography>
            )}
            <Chip
              label={`#${auditIndex + 1}`}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
              }}
            />
            {photoUrl && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  background: 'rgba(0,0,0,0.6)',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ZoomInIcon sx={{ color: '#fff', fontSize: 16 }} />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              flex: { md: '1 1 auto' },
              minWidth: 0,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: TOKENS.slate500,
                }}
              >
                Customer
              </Typography>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: TOKENS.slate900,
                  mt: 0.25,
                }}
              >
                {customerName || '—'}
              </Typography>
            </Box>
            <Box
              sx={{
                background: '#fff',
                border: `1px solid ${TOKENS.slate200}`,
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: TOKENS.slate500,
                }}
              >
                Points
              </Typography>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: tier.color,
                }}
              >
                ${point.points}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  flex: 1,
                  background: '#fff',
                  border: `1px solid ${TOKENS.slate200}`,
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: TOKENS.slate500,
                  }}
                >
                  Machine
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                  }}
                >
                  {point.machine || '—'}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  background: '#fff',
                  border: `1px solid ${TOKENS.slate200}`,
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: TOKENS.slate500,
                  }}
                >
                  Time
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                  }}
                >
                  {formatPointTime(point.signInTime || point.time) || '—'}
                </Typography>
              </Box>
            </Box>
            {/* Audit verdicts write to auditDecision — removed at a
                VIEW-ONLY location, where the audit can be read but not
                decided. See constants/readOnlyAccess.js. */}
            <WriteGuard>
              <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
                <Button
                  fullWidth
                  variant={myDecision === 'FLAGGED' ? 'contained' : 'outlined'}
                  startIcon={<CloseIcon />}
                  onClick={handleFlag}
                  sx={{
                    py: 1.25,
                    background:
                      myDecision === 'FLAGGED' ? TOKENS.red600 : TOKENS.red50,
                    color: myDecision === 'FLAGGED' ? '#fff' : TOKENS.red700,
                    borderColor: TOKENS.red200,
                    '&:hover': {
                      background:
                        myDecision === 'FLAGGED'
                          ? TOKENS.red700
                          : TOKENS.red100,
                    },
                  }}
                >
                  Flag
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<CheckIcon />}
                  onClick={handleApprove}
                  sx={{
                    py: 1.25,
                    background: TOKENS.emerald600,
                    '&:hover': { background: TOKENS.emerald700 },
                  }}
                >
                  Approve
                </Button>
              </Box>
            </WriteGuard>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${TOKENS.slate200}`,
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<ChevronLeftIcon />}
          disabled={auditIndex === 0}
          onClick={goPrev}
        >
          Prev
        </Button>
        <Typography sx={{ fontSize: 10, color: TOKENS.slate500 }}>
          {auditIndex + 1} / {points.length}
        </Typography>
        {isLast ? (
          <Button
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={handleComplete}
            sx={{
              background: TOKENS.emerald600,
              fontWeight: 700,
              '&:hover': { background: TOKENS.emerald700 },
            }}
          >
            Audit Completed
          </Button>
        ) : (
          <Button endIcon={<ChevronRightIcon />} onClick={goNext}>
            Next
          </Button>
        )}
      </Box>
      <Dialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        fullScreen
        PaperProps={{ sx: { background: '#000' } }}
      >
        <AppBar
          position="fixed"
          sx={{ background: 'rgba(0,0,0,0.6)', boxShadow: 0, zIndex: 1 }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, color: '#fff' }}>
                {customerName}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                ${point.points} · Machine {point.machine || '—'}
              </Typography>
            </Box>
            <IconButton onClick={() => setZoomOpen(false)}>
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Toolbar>
        </AppBar>
        <ZoomablePhoto src={photoUrl} alt={customerName} />
      </Dialog>
    </Dialog>
  );
}
